import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Button, Form, LoadingPanel, RadioDetails, RadioGroup } from '@gitbook/spine-react';

import {
    getSiteVisibilityIcon,
    getSiteVisibilityLabel,
    getUpgradeKeyForVisibility,
    siteVisibilityOptions,
} from '../../../../../Components';
import { useSiteContext, useUpgradeSite } from '../../../../../state';
import {
    useVisibilityForm,
    visibilityDescriptions,
    visibilityGroupMap,
} from '../../Visibility/useVisibilityForm';
import { AudienceCardRadio } from '../AudienceCardRadio';

export const SiteVisibility = React.memo(function SiteVisibility() {
    const { site } = useSiteContext();
    const { form, formVisibility, hasChanged, save, getFormGroupDetails } = useVisibilityForm();
    const selectionUpgrade = useUpgradeSite(site, getUpgradeKeyForVisibility(formVisibility));
    const prefersReducedMotion = useReducedMotion();

    const handleChange = (value: api.SiteVisibility) => {
        form.setValue('visibility', value);
        form.setValue('group', visibilityGroupMap[value]);
    };

    return (
        <Form form={form} className="flex flex-col">
            <div className="flex flex-wrap justify-between gap-4">
                <RadioGroup
                    value={visibilityGroupMap[formVisibility]}
                    className="min-w-[24rem] flex-1"
                    onChange={handleChange}
                    aria-labelledby="audience"
                >
                    {Object.values(siteVisibilityOptions).map(({ value }) => {
                        const VisibilityIcon = getSiteVisibilityIcon(value);

                        return (
                            <AudienceCardRadio
                                key={value}
                                value={value}
                                title={getSiteVisibilityLabel(value)}
                                description={visibilityDescriptions[value]}
                                icon={<VisibilityIcon />}
                            />
                        );
                    })}
                </RadioGroup>
                <React.Suspense fallback={<LoadingPanel size="large" />}>
                    <RadioDetails>{getFormGroupDetails()}</RadioDetails>
                </React.Suspense>
            </div>

            <AnimatePresence initial={false}>
                {hasChanged && (
                    <motion.div
                        initial={{ height: 0, opacity: 0, marginTop: 0 }}
                        animate={{
                            height: 'auto',
                            opacity: 1,
                            marginTop: 16,
                            transition: prefersReducedMotion
                                ? { duration: 0 }
                                : {
                                      opacity: { duration: 0.1, delay: 0.1 },
                                      height: { duration: 0.15 },
                                      marginTop: { duration: 0.15 },
                                  },
                        }}
                        exit={{
                            height: 0,
                            opacity: 0,
                            marginTop: 0,
                            transition: prefersReducedMotion
                                ? { duration: 0 }
                                : {
                                      opacity: { duration: 0.1 },
                                      height: { duration: 0.15, delay: 0.1 },
                                      marginTop: { duration: 0.15, delay: 0.1 },
                                  },
                        }}
                        className="flex w-full items-center justify-end gap-2"
                    >
                        <Button
                            label="Update audience"
                            kind="primary"
                            onPress={save}
                            {...selectionUpgrade}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </Form>
    );
});
